import * as Cookies from 'js-cookie';
import { CookieAttributes } from 'js-cookie';
import { getConfig } from './config';
import { parseJWTToken } from './jwt';

// The cookie used by console that tracks a user's last known
// activity that we'll clean up on our end when needed
const LAST_ACTIVITY_COOKIE = 'lastActivityAt';

// When cognito stores things, it will prefix them with
// the following. We'll use this when we want to wipe out
// cognito-related bits from storage
const REGION = getConfig('REACT_APP_REGION');
const USER_POOL_ID = getConfig('REACT_APP_USER_POOL_ID');
const STORAGE_PREFIX = 'CognitoIdentityServiceProvider';
const ID_TOKEN_PATTERN = new RegExp(
  `${STORAGE_PREFIX}\\.(.*)\\.(.*)\\.idToken`
);
const DEVICE_KEY_PATTERN = new RegExp(
  `^${STORAGE_PREFIX}\.[^.]+\.[^.]+\.(?:deviceGroupKey|deviceKey|randomPasswordKey)$`
);
const ISSUER_USER_POOL_PATTERN = new RegExp(
  `https:\/\/cognito-idp\.${REGION}\.amazonaws\.com\/(.*)`
);

/**
 * Given a RegExp, find and return the matching cookie names
 * Intentionally not exported, use helper methods to get
 * specific cookies
 *
 * @param pattern
 */
function findCookieByName(pattern: RegExp): string[] {
  const cookies = Cookies.get();
  return Object.keys(cookies).filter((key) => {
    return pattern.test(key);
  });
}

/**
 * Checks if the provided cookie looks like a cognito auth cookie
 *
 * @param key
 */
function hasStoragePrefix(key: string): boolean {
  return key.substring(0, STORAGE_PREFIX.length) === STORAGE_PREFIX;
}

/**
 * Finds the ID token in the current set of cookies and returns its
 * parsed payload; if zero or multiple cookies were found, then null
 * is returned.
 */
export function getIdTokenFromCookies(): { [k: string]: string } | null {
  const idTokenCookies = findCookieByName(ID_TOKEN_PATTERN);
  if (idTokenCookies.length !== 1) {
    return null;
  }
  return parseJWTToken(Cookies.get(idTokenCookies[0])!);
}

/**
 * Parses cookies for the current client ID
 * If zero or multiple cookies were found, then null is returned.
 */
export function getClientIdFromCookies(): string | null {
  const idToken = getIdTokenFromCookies();
  if (!idToken) {
    return null;
  }

  // The client id is stored in the "aud" attribute of the token
  return idToken.aud ? idToken.aud : null;
}

/**
 * Parses cookies for the current user pool ID
 * If zero or multiple cookies were found, then null is returned.
 */
export function getUserPoolIdFromCookies(): string | null {
  const idToken = getIdTokenFromCookies();
  if (!idToken) {
    return null;
  }

  // The user pool id is part of the "iss" attribute and needs to be
  // parsed out
  const match = idToken.iss && idToken.iss.match(ISSUER_USER_POOL_PATTERN);
  if (!match) {
    return null;
  }

  return match[1];
}

/**
 * Helper method for pulling the cookie attributes from app config
 */
export function getCookieOptions(): CookieAttributes {
  return {
    expires: undefined,
    path: getConfig('REACT_APP_BUILT_COOKIE_PATH'),
    domain: getConfig('REACT_APP_BUILT_COOKIE_DOMAIN'),
    secure: getConfig('REACT_APP_BUILT_COOKIE_SECURE') === 'true'
  };
}

/**
 * Clears up any cookies we care about
 */
export function clearCookieStorage(): void {
  const cookies = Cookies.get();
  const cookieOptions = getCookieOptions();

  // iterate over cookies and remove cognito related bits
  Object.keys(cookies).forEach((key) => {
    if (key && hasStoragePrefix(key) && !DEVICE_KEY_PATTERN.test(key)) {
      Cookies.remove(key, cookieOptions);
    }
  });

  // LOGIN-176: additional cookies to be removed
  Cookies.remove(LAST_ACTIVITY_COOKIE, cookieOptions);
}

/**
 * Clear out any auth cookies that don't belong. These are:
 * - cognito-like cookies that don't use our user pool ID
 * - cognito-like cookies that don't involve the LastAuthUser
 * - old style cookies used to marshal data out of local storage to console
 */
export function clearInvalidCookies(): void {
  const userPoolId = getUserPoolIdFromCookies();
  if (!userPoolId || USER_POOL_ID !== userPoolId) {
    clearStoragePrefixCookies();
    clearLegacyCookies();
  }
}

/**
 * Removes any legacy cookies we no longer need
 */
export function clearLegacyCookies() {
  const cookieOptions = getCookieOptions();
  Cookies.remove('built', cookieOptions);
  Cookies.remove('builtDevice', cookieOptions);
  Cookies.remove('builtId', cookieOptions);
  Cookies.remove('builtRefresh', cookieOptions);
}

export function clearStoragePrefixCookies() {
  const cookies = Cookies.get();
  const cookieOptions = getCookieOptions();
  Object.keys(cookies).forEach((key) => {
    if (hasStoragePrefix(key)) {
      Cookies.remove(key, cookieOptions);
    }
  });
}
